import { type FC, useState } from 'react';

import { Checkbox } from '@base-ui/react/checkbox';
import { Field } from '@base-ui/react/field';
import { Form as BaseForm } from '@base-ui/react/form';
import { Check } from 'lucide-react';

import { ConsentText, LEGAL_DOCS, PrivacyText } from '@entities/legal';
import { trackGoal } from '@shared/lib/analytics';
import { DocumentDialog } from '@shared/ui/DocumentDialog';

import { AttachmentsField } from './AttachmentsField';
import styles from './QuoteForm.module.css';
import { sendQuoteRequest } from '../api/sendQuoteRequest';
import { attachmentType, toBase64 } from '../lib/attachments';
import { isContact } from '../lib/contact';

type Status = 'idle' | 'sending' | 'sent' | 'error';

/** Форма запроса КП: Base UI даёт подписи, связь ошибок с полями и валидацию, вид — наш */
export const QuoteForm: FC = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [started, setStarted] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  // onFormSubmit вызывается только для валидной формы; значения — объект по name полей
  const handleSubmit = async (values: Record<string, unknown>) => {
    const value = (key: string) => {
      const field = values[key];
      return typeof field === 'string' ? field.trim() : '';
    };

    // Скрытое поле-ловушка: человек его не видит, бот заполняет
    if (value('website')) return;

    setStatus('sending');
    try {
      const attachments = await Promise.all(
        files.flatMap((file) => {
          // Список уже отфильтрован при выборе; повторная проверка — только для типа
          const type = attachmentType(file);
          return type ? [toBase64(file).then((data) => ({ name: file.name, type, data }))] : [];
        }),
      );
      await sendQuoteRequest({
        name: value('name'),
        company: value('company'),
        contact: value('contact'),
        message: value('message'),
        attachments,
      });
      // Форма размонтируется — сбрасывать поля не нужно; файлы — сбросить, их держит состояние
      setFiles([]);
      trackGoal('quote_success');
      setStatus('sent');
    } catch {
      trackGoal('quote_error');
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className={styles.root}>
        <h2 className={styles.title}>Запрос коммерческого предложения</h2>
        <div className={styles.sent} role="status">
          <p className={styles.sentTitle}>Заявка отправлена</p>
          <p className={styles.sentText}>Свяжемся с вами в рабочее время.</p>
          <button className={styles.secondaryBtn} type="button" onClick={() => setStatus('idle')}>
            Отправить ещё одну
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Пояснение к звёздочкам — в строке заголовка: видно до заполнения (WCAG 3.3.2), не отдельной
          строкой над полями. Скринридеру обязательность сообщает required — звёздочки от него скрыты */}
      <div className={styles.head}>
        <h2 className={styles.title} id="quote-form-title">
          Запрос коммерческого предложения
        </h2>
        <p className={styles.requiredNote}>
          <span className={styles.required} aria-hidden>
            *
          </span>{' '}
          обязательные поля
        </p>
      </div>
      <BaseForm
        className={`${styles.form} ym-hide-content`}
        aria-labelledby="quote-form-title"
        onFormSubmit={handleSubmit}
        onChange={() => {
          if (!started) {
            trackGoal('quote_start');
            setStarted(true);
          }
        }}
      >
        <Field.Root className={styles.field} name="name">
          <Field.Label className={styles.label}>
            Имя{' '}
            <span className={styles.required} aria-hidden>
              *
            </span>
          </Field.Label>
          <Field.Control
            className={`${styles.input} ym-disable-keys`}
            required
            autoComplete="name"
          />
          <Field.Error className={styles.fieldError} match="valueMissing">
            Укажите имя
          </Field.Error>
        </Field.Root>

        <Field.Root className={styles.field} name="company">
          <Field.Label className={styles.label}>Организация</Field.Label>
          <Field.Control
            className={`${styles.input} ym-disable-keys`}
            autoComplete="organization"
          />
        </Field.Root>

        {/* Пустое поле ловит required — без двойной ошибки */}
        <Field.Root
          className={styles.field}
          name="contact"
          validate={(v) =>
            !v || isContact(v) ? null : 'Телефон — от 10 цифр, почта — в виде name@mail.ru'
          }
        >
          <Field.Label className={styles.label}>
            Телефон или почта{' '}
            <span className={styles.required} aria-hidden>
              *
            </span>
          </Field.Label>
          <Field.Control className={`${styles.input} ym-disable-keys`} required autoComplete="on" />
          <Field.Error className={styles.fieldError} match="valueMissing">
            Укажите телефон или почту
          </Field.Error>
          <Field.Error className={styles.fieldError} match="customError" />
        </Field.Root>

        <Field.Root className={styles.field} name="message">
          <Field.Label className={styles.label}>Опишите задачу</Field.Label>
          <Field.Control
            className={`${styles.input} ym-disable-keys`}
            render={<textarea rows={4} />}
          />
        </Field.Root>

        <AttachmentsField files={files} onChange={setFiles} disabled={status === 'sending'} />

        {/* Согласие по 152-ФЗ: отдельное, не отмечено заранее, без него форма не отправляется */}
        <Field.Root className={styles.field} name="consent">
          <Field.Label className={styles.consentLabel}>
            <Checkbox.Root className={styles.checkbox} required>
              <Checkbox.Indicator className={styles.checkboxIndicator}>
                <Check aria-hidden />
              </Checkbox.Indicator>
            </Checkbox.Root>
            {/* Документы открываются окном поверх формы — введённое не теряется */}
            <span>
              Даю{' '}
              <DocumentDialog
                title={LEGAL_DOCS.consent.title}
                trigger="согласие на обработку персональных данных"
              >
                <ConsentText />
              </DocumentDialog>{' '}
              на условиях{' '}
              <DocumentDialog title={LEGAL_DOCS.privacy.title} trigger="политики">
                <PrivacyText />
              </DocumentDialog>
            </span>
          </Field.Label>
          <Field.Error className={styles.fieldError} match="valueMissing">
            Без согласия отправить заявку нельзя
          </Field.Error>
        </Field.Root>

        {/* Ловушка для ботов — скрыта от людей и скринридеров */}
        <div className={styles.honeypot} aria-hidden>
          <Field.Root name="website">
            <Field.Control className="ym-disable-keys" tabIndex={-1} autoComplete="off" />
          </Field.Root>
        </div>

        {status === 'error' && (
          <p className={styles.formError} role="alert">
            Не получилось отправить. Попробуйте ещё раз или позвоните нам.
          </p>
        )}

        <button className={styles.submitBtn} type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? 'Отправляем…' : 'Отправить заявку'}
        </button>
      </BaseForm>
    </div>
  );
};
